import React, { useState } from 'react';
import { Box, Chip, TextField } from '@mui/material';
import InputText from '../ui/InputText';
import { motion } from 'framer-motion';
import PerButton from './PerButton';
import RejectCheckBottomSheet from './RejectCheckBottomSheet';
import TimePicker from './TimePicker';
import { StaticDatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { useDispatch, useSelector } from 'react-redux';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
import { CommonActions } from '../../../../Reducer/CommonReducer';
import { M_PER_CUSTOM_01_Schema } from '../M_PER_CUSTOM_01/M_PER_CUSTOM_01_Schema';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';

export default function BasicInfoForm({
  errors,
  handleChange,
  rowData2,
  ValidateAll,
  variant = 'BASIC',
}) {
  const { baseInfo } = useSelector((state) => state.permit);
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const bottomSheetState = useSelector((state) => state.bottomSheet);
  const baseInfoFields = M_PER_CUSTOM_01_Schema.fields.baseInfo;

  const [pickerVisible, setPickerVisible] = useState(null);
  const [workerInput, setWorkerInput] = useState('');

  const convertTo24Hour = (timeString) => {
    const [hourStr, minute, period] = timeString.split(/[: ]/);
    let hour = parseInt(hourStr, 10);

    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;

    return `${hour.toString().padStart(2, '0')}:${minute}`;
  };

  const constructionFields = Object.entries(baseInfoFields)
    .filter(
      ([, meta]) =>
        meta?.group === 'construction' &&
        meta?.step === 1 &&
        (!meta?.variants || meta.variants.includes(variant))
    )
    .sort((a, b) => (a[1].order ?? 0) - (b[1].order ?? 0))
    .map(([name, meta]) => ({ name, ...meta, error: errors?.[name] }));

  const workFields = Object.entries(baseInfoFields)
    .filter(
      ([, meta]) =>
        meta?.group === 'work' &&
        meta?.step === 1 &&
        meta?.type !== 'datetime' &&
        (!meta?.variants || meta.variants.includes(variant))
    )
    .sort((a, b) => (a[1].order ?? 0) - (b[1].order ?? 0))
    .map(([name, meta]) => ({ name, ...meta, error: errors?.[name] }));

  const currentWorkers = baseInfo.workList
    ? baseInfo.workList
        .split(',')
        .map((w) => w.trim())
        .filter(Boolean)
    : [];

  return (
    <>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{
          duration: 1,
          ease: [0.25, 0.1, 0.25, 1.0],
        }}
      >
        {rowData2?.returnStatus === 'Y' ? (
          <>
            작업허가서가 반려되었어요. <br /> 반려 내용을 확인해주세요.
          </>
        ) : (
          <>
            {userInfo.userNm} 님의 작업허가서 <br />
            작성을 시작할게요.
          </>
        )}
      </motion.div>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{
          duration: 0.5,
          ease: 'easeOut',
          delay: 1,
        }}
      >
        {rowData2?.returnStatus === 'Y' && (
          <button
            style={{
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              borderBottom: '1px solid #b9b9b9ff',
              backgroundColor: 'transparent',
              fontSize: '16px',
              color: '#939393',
              fontWeight: 'bold',
            }}
            onClick={() => {
              dispatch(
                BottomSheetActions.SetBottomSheetState({
                  bottom: true,
                  type: 'rejecCheck',
                })
              );
            }}
          >
            반려 내용 확인하기
          </button>
        )}
        <p style={{ fontSize: '16px', color: '#939393' }}>
          아래 사항들을 작성해주세요.
        </p>
        <hr style={{ border: 'none', borderTop: '2px solid #F8EFEF' }} />
        <Box>
          <p style={{ paddingBottom: '0.5rem' }}>수급사 현황 (공사업체 정보)</p>
          {constructionFields.map((field) => {
            if (field.type === 'date') {
              return (
                <div key={field.name}>
                  <Box>
                    <p
                      style={{
                        fontWeight: 'normal',
                        fontSize: '14px',
                        color: '#000',
                        marginBottom: '4px',
                      }}
                    >
                      {field.labelByVariant?.[variant] || field.label}
                    </p>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'flex-end',
                      }}
                    >
                      <button
                        onClick={() =>
                          setPickerVisible((prev) =>
                            prev === field.name ? null : field.name
                          )
                        }
                        style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          border: 'none',
                          padding: '8px 10px',
                          borderRadius: '5px',
                          color:
                            pickerVisible === field.name ? '#007AFF' : '#333',
                        }}
                      >
                        {baseInfo.permissionDate || '날짜 선택'}
                      </button>
                    </Box>
                  </Box>
                  {pickerVisible === field.name && (
                    <StaticDatePicker
                      value={dayjs(baseInfo.permissionDate)}
                      minDate={dayjs()}
                      onChange={(newValue) => {
                        handleChange(field.name, newValue.format('YYYY-MM-DD'));
                        setPickerVisible(null);
                      }}
                      slotProps={{
                        toolbar: { hidden: true },
                        actionBar: { actions: [] },
                      }}
                    />
                  )}
                </div>
              );
            } // 일반 텍스트 필드

            return (
              <InputText
                key={field.name}
                label={field.labelByVariant?.[variant] || field.label}
                value={baseInfo[field.name]}
                onChange={(e) => {
                  let value = e.target.value;

                  if (field.format === 'phone') {
                    value = value.replace(/[^0-9]/g, '');
                    if (value.length > 3 && value.length <= 7) {
                      value = value.replace(/(\d{3})(\d+)/, '$1-$2');
                    } else if (value.length > 7) {
                      value = value.replace(/(\d{3})(\d{4})(\d+)/, '$1-$2-$3');
                    }
                    value = value.slice(0, 13);
                  }

                  if (field.format === 'numeric') {
                    value = value.replace(/[^0-9]/g, '');
                  }

                  handleChange(field.name, value);
                }}
                placeholder={
                  field.placeholderByVariant?.[variant] || field.placeholder
                }
                error={field.error}
                maxLength={field.maxLength}
                inputMode={field.inputMode}
              />
            );
          })}
          <p style={{ paddingBottom: '0.5rem' }}>작업 개요</p>
          {/* 시작/종료 시간 */}
          {[
            {
              key: 'workStartDate',
              label: baseInfoFields.workStartDate.label,
              picker: 'date',
              timePicker: 'time',
            },
            {
              key: 'workEndDate',
              label: baseInfoFields.workEndDate.label,
              picker: 'endDate',
              timePicker: 'endTime',
            },
          ].map((item) => (
            <div key={item.key}>
              <Box>
                <p style={{ fontSize: '14px', marginBottom: '4px' }}>
                  {item.label}
                </p>
                <Box
                  sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}
                >
                  {(item.key === 'workStartDate' || variant === 'CUSTOM') && (
                    <button
                      onClick={() =>
                        setPickerVisible((prev) =>
                          prev === item.picker ? null : item.picker
                        )
                      }
                      style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color:
                          pickerVisible === item.picker ? '#007AFF' : '#333',
                      }}
                    >
                      {baseInfo[item.key]?.split(' ')[0]}
                    </button>
                  )}
                  <button
                    onClick={() =>
                      setPickerVisible((prev) =>
                        prev === item.timePicker ? null : item.timePicker
                      )
                    }
                    style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color:
                        pickerVisible === item.timePicker ? '#007AFF' : '#333',
                    }}
                  >
                    {baseInfo[item.key]?.split(' ')[1]}
                  </button>
                </Box>
              </Box>

              {pickerVisible === item.picker && (
                <StaticDatePicker
                  value={dayjs(baseInfo[item.key]?.split(' ')[0])}
                  minDate={
                    item.key === 'workEndDate'
                      ? dayjs(baseInfo.workStartDate?.split(' ')[0])
                      : dayjs()
                  }
                  onChange={(newValue) => {
                    handleChange(
                      item.key,
                      `${newValue.format('YYYY-MM-DD')} ${baseInfo[item.key]?.split(' ')[1]}`
                    );
                    setPickerVisible(null);
                  }}
                />
              )}

              {pickerVisible === item.timePicker && (
                <TimePicker
                  value={{
                    hour: dayjs(
                      baseInfo[item.key]?.split(' ')[1],
                      'HH:mm'
                    ).format('hh'),
                    minute: dayjs(
                      baseInfo[item.key]?.split(' ')[1],
                      'HH:mm'
                    ).format('mm'),
                    period:
                      dayjs(
                        baseInfo[item.key]?.split(' ')[1],
                        'HH:mm'
                      ).hour() >= 12
                        ? 'PM'
                        : 'AM',
                  }}
                  onChange={(formatted) => {
                    handleChange(
                      item.key,
                      `${baseInfo[item.key]?.split(' ')[0]} ${convertTo24Hour(formatted)}`
                    );
                  }}
                />
              )}
            </div>
          ))}

          {/* 작업명단 */}
          {workFields.map((field) => {
            if (field.type === 'workerList') {
              return (
                <Box key={field.name} sx={{ mb: 2 }}>
                  <p style={{ fontSize: '14px', marginBottom: '8px' }}>
                    {field.labelByVariant?.[variant] || field.label}
                  </p>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      value={workerInput}
                      onChange={(e) => setWorkerInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && workerInput.trim()) {
                          e.preventDefault();
                          const name = workerInput.trim();
                          if (!currentWorkers.includes(name)) {
                            const newWorkers = [...currentWorkers, name];
                            handleChange(field.name, newWorkers.join(', '));
                            setWorkerInput('');
                          }
                        }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <button
                      onClick={() => {
                        if (workerInput.trim()) {
                          const name = workerInput.trim();
                          if (!currentWorkers.includes(name)) {
                            const newWorkers = [...currentWorkers, name];
                            handleChange(field.name, newWorkers.join(', '));
                            setWorkerInput('');
                          } else {
                            dispatch(
                              showSnackbar({
                                message: '이미 등록된 이름입니다.',
                                type: 'warning',
                              })
                            );
                          }
                        }
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#000',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                      }}
                    >
                      추가
                    </button>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {currentWorkers.map((worker, index) => (
                      <Chip
                        key={`${worker}-${index}`}
                        label={worker}
                        onDelete={() => {
                          const newWorkers = currentWorkers.filter(
                            (_, i) => i !== index
                          );
                          handleChange(field.name, newWorkers.join(', '));
                        }}
                      />
                    ))}
                  </Box>
                  {field.error && (
                    <p style={{ color: '#d32f2f', fontSize: '12px' }}>
                      {field.error}
                    </p>
                  )}
                </Box>
              );
            }

            return (
              <InputText
                key={field.name}
                label={field.labelByVariant?.[variant] || field.label}
                value={baseInfo[field.name]}
                onChange={(e) => {
                  let value = e.target.value;

                  if (field.format === 'numeric') {
                    value = value.replace(/[^0-9]/g, '');
                  }

                  handleChange(field.name, value);
                }}
                placeholder={
                  field.placeholderByVariant?.[variant] || field.placeholder
                }
                error={field.error}
                maxLength={field.maxLength}
                inputMode={field.inputMode}
              />
            );
          })}
        </Box>
        <PerButton
          onCancel={() =>
            dispatch(
              BottomSheetActions.SetBottomSheetState({
                bottom: true,
                type: 'backClick',
              })
            )
          }
          onClick={() => {
            // if (!isValidWorkDuration()) {
            //  dispatch(
            //   showSnackbar({
            //    message: `최대 ${MAX_WORK_HOURS}시간까지만 신청 가능합니다.`,
            //    type: 'warning',
            //   })
            //  );
            //  return;
            // }
            if (ValidateAll()) {
              dispatch(CommonActions.increaseStep());
            }
          }}
        />
        {bottomSheetState.bottom && bottomSheetState.type === 'rejecCheck' && (
          <RejectCheckBottomSheet returnDetail={rowData2?.returnReason} />
        )}
      </motion.div>
    </>
  );
}
